import {SupportedLocales} from '@gl/lit/types/locale.js';

export const journeysLocale = {
  [SupportedLocales.EN]: {
    journeysTable: {
      noMatches: {
        headline: 'There are no journeys.',
        caption: 'Looks like you have not added a journey.',
      },
      tagsFilter: {
        placeholder: 'Tags',
        emptyMessage: 'There are no tags available to filter with.',
      },
      canvasGroup: {
        maxSubnodesTooltip: (maxSubnodes: number) =>
          `You cannot have more than ${maxSubnodes} subnodes`,
      },
      manualMode: {
        runUntilThisNode: 'Run Until This Node',
        runOnlyThisNode: 'Run Only This Node',
        runFromThisNode: 'Run From This Node',
      },
      datasetGroupFilter: {
        placeholder: 'Dataset Group',
      },
      destinationFilter: {
        placeholder: 'Destination',
      },
      creatorFilter: {
        placeholder: 'Creator',
      },
      createButton: 'Create a Journey',
      searchText: 'Search',
    },
    realtimeMode: 'Realtime Mode',
    config: {
      preview: 'Preview',
      apply: 'Apply',
    },
    settingsConfig: {
      journey: 'Journey',
      journeyType: 'Journey Type',
      datasetGroup: 'Dataset Group',
      createDatasetGroup: 'Create Dataset Group',
      selectAudience: 'Select Audience',
      createAudience: 'Create Audience',
      cancel: 'Cancel',
      confirm: 'Confirm',
      selectRealtimeEvent: 'Select Realtime Event',
      registerNewEvent: 'Register New Event',
      whenWillJourneyStartRunning: 'When will journey start running?',
    },
    reportHead: {
      heading: 'Journeys Report',
      lastUpdated: 'Last Updated: ',
      errorMessage: 'There was an error with your request.',
    },
    personalizationFieldsConfig: {
      subtitle:
        'Select additional fields you would like to export to Destination as custom fields.',
      subheader:
        ' Note: Fields will only be updated while audience is live. If you’d like to have fields always updated in your destination for all audiences, create a Sync.',
      additionalFieldsHeader: 'Additional Fields',
      additionalFieldsDescription:
        'Add additional fields to your export to {destination}. These can be used in your destination for personalization, and other purposes. Please note that if you alias a field to an existing column in your destination, we will attempt to write the new value to that attribute.',
    },
    destinationConfigWindow: {
      title: 'Configure Destination',
      customAttributesTitle: 'Additional Custom Attributes',
      customAttributesDescription:
        'Want to track extra attributes for users being exported to this destination?  These will be included in the snapshot tables during export.',
    },
    variationConfigWindow: {
      settingsTab: 'Settings',
    },
    entryConfigWindow: {
      setupTab: 'Journey Setup',
    },
    customAttributes: {
      label: 'Add Custom Attribute',
      label_tag: 'Custom Attribute',
      errors: {
        emptyLabels: 'Labels cannot be empty',
        specialChars:
          'Labels can only contain letters, numbers, spaces and underscores',
        duplicateLabels: 'Duplicate aliases are not allowed',
      },
    },
    crmConfigWindow: {
      title: 'CRM Action',
      selectMatchFieldPrompt:
        'Select fields you would like to use to match with the destination',
    },
    reportChips: {
      active: 'Number of users currently in this step',
      completed: 'Number of users that have completed this step',
      exited: 'Number of users that have exited due to Global Exit Criteria',
    },
    crmNode: {
      noSettings: 'You have not selected any criteria or settings',
      noSettingsTitle: 'Empty Node',
      evaluationSettingsApplied: 'Evaluation settings applied',
      selectionPrompt: 'Select criteria or set evaluation settings',
    },
    destinationNode: {
      invalidConfiguration: 'You have invalid configuration',
      invalidConfigurationTitle: 'Invalid Node',
    },
    criteriaNodeSettings: {
      customAttributesTitle: 'Custom Attributes',
      customAttributesDescription:
        'Add extra metadata to users that match, or do not match the criteria specified for this node.',
      paths: {
        yes: 'Attributes applied to users that match the specified criteria.',
        no: 'Attributes applied to users that do not match the specified criteria.',
      },
      invalid:
        'Either a condition, evaluation setting or limit must be applied.',
    },
    abandonSelectionsMessage:
      'Are you sure you want to navigate to the journey page?',
    confirmation: (action: string) => {
      return `Are you sure you want to ${action} these journeys?`;
    },
    additionalFieldSelect: {
      addPrimaryTableFields: 'Add table fields',
      tooltip: {
        content:
          'Only fields from your primary table will be selected. GrowthLoop generated fields or custom attributes will not be selected.',
      },
      alias: {
        placeholder: 'Alias',
        validation: {
          empty: 'Alias name cannot be empty',
          specialChars: 'Special characters are not allowed in alias names',
          unique: 'Alias names must be unique.',
        },
      },
      errors: {
        invalidQuery: 'Invalid query state.',
        invalidQueryState: 'Invalid query.',
        fetchError: 'Error fetching field values for:{datasetId}',
      },
    },
    paidMediaExport: {
      tableNameValidation: {
        alreadyInUse:
          'This table name is already in use. Please choose a unique name.',
        isUnique: 'This table name is unique and available for use.',
        invalidCharacters:
          'Table name can only contain letters (uppercase and lowercase), numbers, and underscores',
      },
    },
    errorPanel: {
      noErrors: 'No errors found for this node',
      noErrorsOverview: 'No errors found for this journey',
      alertsOverview: 'Alerts Overview',
      errors: 'Errors',
      refreshed: 'Refreshed',
      refresh: 'Refresh',
      default: 'Default',
    },
    journeySettings: {
      entryAudienceConfirmModal: {
        content:
          'Selecting a different audience could cause issues with the nodes and schema. Please ensure that your journey is compatible with the schema on the selected audience.',
      },
    },
    journeyNodeOptions: {
      rename: 'Rename',
      delete: 'Delete',
    },
  },
  [SupportedLocales.ES]: {
    journeysTable: {
      noMatches: {
        headline: 'No hay viajes.',
        caption: 'Parece que no has agregado un viaje.',
      },
      tagsFilter: {
        placeholder: 'Etiquetas',
        emptyMessage: 'No hay etiquetas disponibles para filtrar.',
      },
      datasetGroupFilter: {
        placeholder: 'Grupo de Conjuntos de Datos',
      },
      canvasGroup: {
        maxSubnodesTooltip: (maxSubnodes: number) =>
          `No puedes tener más de ${maxSubnodes} subnodos`,
      },
      manualMode: {
        runUntilThisNode: 'Ejecutar Hasta Este Nodo',
        runOnlyThisNode: 'Ejecutar Solo Este Nodo',
        runFromThisNode: 'Ejecutar Desde Este Nodo',
      },
      destinationFilter: {
        placeholder: 'Destino',
      },
      creatorFilter: {
        placeholder: 'Creador',
      },
      createButton: 'Crear un Viaje',
      searchText: 'Buscar',
    },
    realtimeMode: 'Modo de tiempo real',
    config: {
      preview: 'Vista previa',
      apply: 'Aplicar',
    },
    crmConfigWindow: {
      title: 'Acción CRM',
      selectMatchFieldPrompt:
        'Seleccione los campos que le gustaría usar para hacer coincidir con el destino',
    },
    settingsConfig: {
      journey: 'Viaje',
      journeyType: 'Tipo de Viaje',
      datasetGroup: 'Grupo de Conjunto de Datos',
      createDatasetGroup: 'Crear Grupo de Conjunto de Datos',
      selectAudience: 'Seleccionar Audiencia',
      createAudience: 'Crear Audiencia',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      selectRealtimeEvent: 'Seleccionar Evento en Tiempo Real',
      registerNewEvent: 'Registrar Nuevo Evento',
      whenWillJourneyStartRunning: '¿Cuando empezará el viaje?',
    },
    destinationNode: {
      invalidConfiguration: 'Tienes una configuración no válida',
      invalidConfigurationTitle: 'Nodo inválido',
    },
    reportHead: {
      heading: 'Informe de Viajes',
      lastUpdated: 'Última Actualización: ',
      errorMessage: 'Hubo un error con tu solicitud.',
    },
    personalizationFieldsConfig: {
      subtitle:
        'Selecciona campos adicionales que te gustaría exportar a Destino como campos personalizados.',
      subheader:
        ' Nota: Los campos solo se actualizarán mientras la audiencia esté activa. Si deseas que los campos siempre se actualicen en tu destino para todas las audiencias, crea una Sincronización.',
      additionalFieldsHeader: 'Campos Adicionales',
      additionalFieldsDescription:
        'Agrega campos adicionales a tu exportación a {destination}. Estos pueden ser utilizados en tu destino para personalización y otros propósitos. Por favor, ten en cuenta que si asignas un alias que ya existe en tu destino a un campo, intentaremos escribir el nuevo valor en ese atributo.',
    },
    destinationConfigWindow: {
      title: 'Configurar Destino',
      settingsTab: 'Ajustes',
      settingsTabDescription:
        '¿Desea realizar un seguimiento de atributos adicionales para los usuarios que se exportan a este destino? Estos se incluirán en las tablas de instantáneas durante la exportación.',
      customAttributesTitle: 'Atributos Personalizados Adicionales',
      customAttributesDescription:
        '¿Desea realizar un seguimiento de atributos adicionales para los usuarios que se exportan a este destino? Estos se incluirán en las tablas de instantáneas durante la exportación.',
    },
    variationConfigWindow: {
      settingsTab: 'Ajustes',
    },
    entryConfigWindow: {
      setupTab: 'Configuración',
    },
    customAttributes: {
      label: 'Añadir Atributo Personalizado',
      label_tag: 'Atributo Personalizado',
      errors: {
        duplicateLabels: 'Los alias duplicados no están permitidos',
        emptyLabels: 'Los alias no pueden estar vacíos',
        specialChars:
          'Los alias solo pueden contener letras, números, espacios y guiones bajos',
      },
    },
    reportChips: {
      active: 'Número de usuarios actualmente en este paso',
      completed: 'Número de usuarios que han completado este paso',
      exited:
        'Número de usuarios que han salido debido a los Criterios de Salida Global',
    },
    criteriaNodeSettings: {
      customAttributesTitle: 'Atributos Personalizados',
      customAttributesDescription:
        'Rastrear atributos adicionales para los usuarios que coincidan, o no coincidan, con los criterios para este nodo.',
      paths: {
        yes: 'Atributos que se aplicarán si los criterios coinciden.',
        no: 'Atributos que se aplicarán si los criterios no coinciden.',
      },
      invalid:
        'Debe aplicar una condición, configuración de evaluación o límite.',
    },
    abandonSelectionsMessage:
      '¿Estás seguro de que quieres navegar a la página del viaje?',
    confirmation: (action: string) => {
      return `¿Estás seguro de que quieres ${action} estos viajes?`;
    },
    additionalFieldSelect: {
      addPrimaryTableFields: 'Añadir campos de tabla',
      tooltip: {
        content:
          'Solo se seleccionarán los campos de tu tabla principal. Los campos generados por GrowthLoop o atributos personalizados no se seleccionarán.',
      },
      alias: {
        placeholder: 'Alias',
        validation: {
          empty: 'El nombre del alias no puede estar vacío',
          specialChars:
            'No se permiten caracteres especiales en los nombres de alias',
          unique: 'Los nombres de alias deben ser únicos.',
        },
      },
      errors: {
        invalidQuery: 'Estado de consulta inválido.',
        invalidQueryState: 'Consulta inválida.',
        fetchError: 'Error al obtener valores de campo para:{datasetId}',
      },
    },
    paidMediaExport: {
      tableNameValidation: {
        alreadyInUse:
          'Este nombre de tabla ya está en uso. Por favor elige un nombre único.',
        isUnique:
          'Este nombre de tabla es único y está disponible para su uso.',
        invalidCharacters:
          'El nombre de la tabla solo puede contener letras, números y guiones bajos',
      },
    },
    errorPanel: {
      noErrors: 'No hay errores encontrados para este nodo',
      noErrorsOverview: 'No hay errores encontrados para este viaje',
      alertsOverview: 'Resumen de Alertas',
      errors: 'Errores',
      refreshed: 'Refresado',
      refresh: 'Refresar',
      default: 'Predeterminado',
    },
    journeySettings: {
      entryAudienceConfirmModal: {
        content:
          'Seleccionar una audiencia diferente podría causar problemas con los nodos y el esquema. Por favor, asegúrate de que tu viaje sea compatible con el esquema de la audiencia seleccionada.',
      },
    },
    journeyNodeOptions: {
      rename: 'Renombrar',
      delete: 'Eliminar',
    },
  },
};
